import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Chessground } from 'chessground';
import { Api } from 'chessground/api';
import { Chess } from 'chess.js';
import { Color, Key } from 'chessground/types';
import { DrawShape } from 'chessground/draw';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleLeft,
  faAngleRight,
  faAnglesLeft,
  faAnglesRight,
  faUpDown,
} from '@fortawesome/free-solid-svg-icons';

interface Ply {
  san: string;
  fen: string;
  from: Key;
  to: Key;
  color: 'w' | 'b';
  moveNo: number;
}

// Interactive opening walkthrough on the server-rendered opening pages (its own bundle), replacing the
// static board. Replays the line's SAN with chess.js and lets the visitor step through it, with a
// green arrow on each move. See docs/02_areas/seo.
export default function OpeningBoard({ moves }: { moves: string[] }): React.ReactNode {
  const startFen = useMemo(() => new Chess().fen(), []);

  const plies = useMemo<Ply[]>(() => {
    const chess = new Chess();
    const out: Ply[] = [];
    for (const san of moves) {
      try {
        const m = chess.move(san);
        if (!m) break;
        out.push({
          san: m.san,
          fen: chess.fen(),
          from: m.from as Key,
          to: m.to as Key,
          color: m.color,
          moveNo: Math.floor(out.length / 2) + 1,
        });
      } catch {
        break; // stop at the first move chess.js can't make; render what we have
      }
    }
    return out;
  }, [moves]);

  // Start on the final position, the one the page is about.
  const [idx, setIdx] = useState(plies.length);
  const [orientation, setOrientation] = useState<Color>('white');
  const boardRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<Api | null>(null);

  const go = useCallback(
    (n: number) => setIdx(() => Math.max(0, Math.min(plies.length, n))),
    [plies.length],
  );

  useEffect(() => {
    if (!boardRef.current) return;
    const cg = Chessground(boardRef.current, {
      viewOnly: true,
      coordinates: false,
      orientation,
      animation: { enabled: true, duration: 200 },
      drawable: { enabled: false, visible: true },
    });
    apiRef.current = cg;
    return () => cg.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const cg = apiRef.current;
    if (!cg) return;
    const cur = idx > 0 ? plies[idx - 1] : null;
    cg.set({
      fen: cur ? cur.fen : startFen,
      orientation,
      lastMove: cur ? [cur.from, cur.to] : undefined,
    });
    const shapes: DrawShape[] = cur ? [{ orig: cur.from, dest: cur.to, brush: 'green' }] : [];
    cg.setAutoShapes(shapes);
  }, [idx, orientation, plies, startFen]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') go(idx - 1);
      else if (e.key === 'ArrowRight') go(idx + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [idx, go]);

  // Group plies into numbered move pairs for the scoresheet.
  const rows = useMemo(() => {
    const r: { no: number; white?: Ply; whiteIdx?: number; black?: Ply; blackIdx?: number }[] = [];
    plies.forEach((p, i) => {
      const row = r[p.moveNo - 1] ?? { no: p.moveNo };
      if (p.color === 'w') {
        row.white = p;
        row.whiteIdx = i + 1;
      } else {
        row.black = p;
        row.blackIdx = i + 1;
      }
      r[p.moveNo - 1] = row;
    });
    return r;
  }, [plies]);

  return (
    <div className="op-cg">
      <div className="op-cg-holder">
        <div className="op-cg-board" ref={boardRef} />
      </div>
      <div className="op-cg-side">
        <div className="op-cg-controls d-flex justify-content-center">
          <button
            type="button"
            className="btn btn-secondary p-1 me-3"
            style={{ width: '50px' }}
            onClick={() => setOrientation((o) => (o === 'white' ? 'black' : 'white'))}
            aria-label="Flip board"
          >
            <FontAwesomeIcon icon={faUpDown} style={{ color: '#ffffff' }} />
          </button>
          <button
            type="button"
            className="btn btn-secondary p-1 me-3"
            style={{ width: '50px' }}
            onClick={() => go(0)}
            disabled={idx === 0}
            aria-label="First move"
          >
            <FontAwesomeIcon icon={faAnglesLeft} style={{ color: '#ffffff' }} />
          </button>
          <button
            type="button"
            className="btn btn-secondary p-1 me-3"
            style={{ width: '50px' }}
            onClick={() => go(idx - 1)}
            disabled={idx === 0}
            aria-label="Previous move"
          >
            <FontAwesomeIcon icon={faAngleLeft} style={{ color: '#ffffff' }} />
          </button>
          <button
            type="button"
            className="btn btn-secondary p-1 me-3"
            style={{ width: '50px' }}
            onClick={() => go(idx + 1)}
            disabled={idx === plies.length}
            aria-label="Next move"
          >
            <FontAwesomeIcon icon={faAngleRight} style={{ color: '#ffffff' }} />
          </button>
          <button
            type="button"
            className="btn btn-secondary p-1"
            style={{ width: '50px' }}
            onClick={() => go(plies.length)}
            disabled={idx === plies.length}
            aria-label="Last move"
          >
            <FontAwesomeIcon icon={faAnglesRight} style={{ color: '#ffffff' }} />
          </button>
        </div>
        <div className="op-cg-moves" aria-label="Opening moves">
          {rows.map((row) => (
            <span className="op-cg-row" key={row.no}>
              <span className="op-cg-no">{row.no}.</span>
              {row.white && (
                <button
                  type="button"
                  className={'op-cg-move' + (idx === row.whiteIdx ? ' is-current' : '')}
                  onClick={() => go(row.whiteIdx!)}
                >
                  {row.white.san}
                </button>
              )}
              {row.black && (
                <button
                  type="button"
                  className={'op-cg-move' + (idx === row.blackIdx ? ' is-current' : '')}
                  onClick={() => go(row.blackIdx!)}
                >
                  {row.black.san}
                </button>
              )}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
